import { UIBlock } from './types';

/**
 * Adapt AgentCore response into UIBlock[].
 * Tier 1: Structured JSON with `blocks` array → render confidently
 * Tier 2: Structured JSON with known fields (summary, metrics, table, chart) → parse
 * Tier 3: Free-form text → render as markdown text block
 */
export function adaptAgentCoreResponse(raw: any): UIBlock[] {
  // If raw is a string, try JSON parse first
  if (typeof raw === 'string') {
    let text = raw.trim();

    // Strip wrapping quotes (double-stringified) — try multiple rounds
    let maxAttempts = 3;
    while (maxAttempts-- > 0 && typeof text === 'string' && text.startsWith('"') && text.endsWith('"')) {
      try { text = JSON.parse(text); } catch { break; }
    }

    // Strip a code fence ONLY when it wraps the entire payload (a ```json
    // envelope around a structured response). Anchored to the whole string —
    // not per-line — so that genuine inner code blocks in free-form markdown
    // answers (e.g. ```bash command snippets```) are preserved rather than
    // having their fences deleted, which would flatten them into prose.
    // Only unwrap when the captured payload actually looks like JSON. Because
    // `$` here anchors to end-of-string, a free-form answer that both opens
    // and closes with a bare ``` fence would otherwise match end-to-end and
    // have its first/last fences stripped and middle flattened — the same
    // corruption this guard prevents. Requiring the inner payload to start
    // with { or [ ensures genuine code-block answers are never touched.
    if (typeof text === 'string') {
      const wrapped = text.match(/^```(?:json)?\s*\n([\s\S]*?)\n?```$/);
      if (wrapped) {
        const inner = wrapped[1].trim();
        if (inner.startsWith('{') || inner.startsWith('[')) {
          text = inner;
        }
      }
    }

    // Try JSON parse BEFORE unescaping \\n (unescaping can break JSON strings)
    // 1. Try to find and parse JSON object with "blocks"
    if (typeof text === 'string') {
      const jsonMatch = text.match(/\{[\s\S]*"blocks"[\s\S]*\}/);
      if (jsonMatch) {
        try {
          const parsed = JSON.parse(jsonMatch[0]);
          return adaptStructured(parsed);
        } catch {}
      }

      // 2. Try direct JSON parse
      if (text.startsWith('{') || text.startsWith('[')) {
        try {
          const parsed = JSON.parse(text);
          return adaptStructured(parsed);
        } catch {}
      }

      // 3. JSON parse failed — now unescape and try once more
      const unescaped = text.replace(/\\\\n/g, '\n').replace(/\\\\t/g, '\t')
                            .replace(/\\n/g, '\n').replace(/\\t/g, '\t');

      // Retry JSON parse after unescaping
      try {
        const parsed = JSON.parse(unescaped);
        return adaptStructured(parsed);
      } catch {}

      // Tier 3.5: Detect markdown tables with time-series data → convert to chart + text
      const mdBlocks = extractMarkdownTableAsChart(unescaped);
      if (mdBlocks) return mdBlocks;

      // Render as markdown text
      return [{ type: 'text', text: unescaped, markdown: true }];
    }

    return [{ type: 'text', text: String(text), markdown: true }];
  }

  // Already an object
  return adaptStructured(raw);
}

function adaptStructured(data: any): UIBlock[] {
  // Tier 1: Has explicit blocks array
  if (data.blocks && Array.isArray(data.blocks)) {
    const blocks = data.blocks.map(validateBlock).filter(Boolean) as UIBlock[];
    return blocks.map(unescapeBlockText);
  }

  // Tier 1.5: Troubleshooter conclusion (detected_cause + recommendations)
  if (data.detected_cause !== undefined && Array.isArray(data.recommendations)) {
    return formatTroubleshooterConclusion(data);
  }

  const blocks: UIBlock[] = [];

  // Tier 2: Known structured fields
  if (data.summary) {
    blocks.push({ type: 'text', text: data.summary, markdown: true });
  }

  if (data.metrics && Array.isArray(data.metrics)) {
    for (const m of data.metrics) {
      blocks.push({ type: 'metric', label: m.label, value: String(m.value), trend: m.trend });
    }
  }

  if (data.table && Array.isArray(data.table.columns) && Array.isArray(data.table.rows)) {
    blocks.push({ type: 'table', columns: data.table.columns, rows: data.table.rows });
  }

  if (data.tables && Array.isArray(data.tables)) {
    for (const t of data.tables) {
      if (Array.isArray(t.columns) && Array.isArray(t.rows)) {
        blocks.push({ type: 'table', columns: t.columns, rows: t.rows });
      }
    }
  }

  if (data.chart && Array.isArray(data.chart.data) && Array.isArray(data.chart.yKeys)) {
    blocks.push({
      type: 'chart',
      chartType: data.chart.type || data.chart.chartType || 'line',
      data: data.chart.data,
      xKey: data.chart.xKey,
      yKeys: data.chart.yKeys,
    });
  }

  if (data.callout) {
    blocks.push({
      type: 'callout',
      level: data.callout.level || 'info',
      title: data.callout.title,
      text: data.callout.text,
    });
  }

  if (data.code) {
    blocks.push({ type: 'code', language: data.code.language || 'text', content: data.code.content });
  }

  if (data.imageUrl) {
    blocks.push({ type: 'image', url: data.imageUrl, alt: data.imageAlt });
  }

  if (data.actions && Array.isArray(data.actions)) {
    blocks.push({ type: 'actions', actions: data.actions });
  }

  if (data.timeline && Array.isArray(data.timeline)) {
    blocks.push({ type: 'timeline', items: data.timeline });
  }

  // Tier 3: Nothing matched — show as JSON
  if (!blocks.length) {
    if (data.message || data.text || data.output) {
      const text = data.message || data.text || data.output;
      return [{ type: 'text', text, markdown: true }];
    }
    blocks.push({ type: 'json', data });
  }

  return blocks;
}

function unescapeBlockText(block: UIBlock): UIBlock {
  const b = { ...block } as any;
  if (typeof b.text === 'string') {
    b.text = b.text.replace(/\\n/g, '\n').replace(/\\t/g, '\t');
  }
  if (typeof b.title === 'string') {
    b.title = b.title.replace(/\\n/g, '\n').replace(/\\t/g, '\t');
  }
  if (typeof b.content === 'string') {
    b.content = b.content.replace(/\\n/g, '\n').replace(/\\t/g, '\t');
  }
  return b as UIBlock;
}

function validateBlock(block: any): UIBlock | null {
  if (!block || !block.type) return null;
  if (block.type === 'text' && typeof block.text !== 'string') return null;
  if (block.type === 'table' && (!Array.isArray(block.columns) || !Array.isArray(block.rows))) return null;
  if (block.type === 'chart' && (!Array.isArray(block.data) || !Array.isArray(block.yKeys))) return null;
  if (block.type === 'callout' && typeof block.text !== 'string') return null;
  if (block.type === 'code' && typeof block.content !== 'string') return null;
  if (block.type === 'image' && typeof block.url !== 'string') return null;
  if (block.type === 'actions' && !Array.isArray(block.actions)) return null;
  if (block.type === 'timeline' && !Array.isArray(block.items)) return null;
  return block as UIBlock;
}

/**
 * Format a troubleshooter conclusion JSON into readable markdown blocks.
 */
function formatTroubleshooterConclusion(data: any): UIBlock[] {
  const blocks: UIBlock[] = [];
  const cause = data.detected_cause || 'unknown';
  const isNonConclusive = ['no_cause_identified', 'no_issue_detected', 'insufficient_data', 'unknown'].includes(cause);

  // Header
  blocks.push({
    type: 'text',
    text: isNonConclusive
      ? '## ✅ No Root Cause Identified\n\nThe cluster appears healthy based on the diagnostics performed.'
      : `## 🔍 Detected Cause: \`${cause.replace(/_/g, ' ')}\``,
    markdown: true,
  });

  // Summary (findings detail)
  if (data.summary) {
    blocks.push({ type: 'text', text: data.summary, markdown: true });
  }

  // Recommendations
  if (data.recommendations?.length) {
    let recsMarkdown = '### Recommendations\n\n';
    data.recommendations.forEach((group: string[], gi: number) => {
      if (data.recommendations.length > 1) {
        recsMarkdown += `**Step ${gi + 1}:**\n`;
      }
      group.forEach((step: string) => {
        recsMarkdown += `- ${step}\n`;
      });
      recsMarkdown += '\n';
    });
    blocks.push({ type: 'text', text: recsMarkdown, markdown: true });
  }

  // Validation summary (if present, show condensed)
  const validation = data._validation_pipeline;
  if (validation?.consolidated) {
    const c = validation.consolidated;
    const confidence = Math.round((c.confidence || 0) * 100);
    blocks.push({
      type: 'callout',
      level: c.decision === 'pass' ? 'success' : 'warning',
      text: `Validation: **${c.decision.toUpperCase()}** (${confidence}% confidence)`,
      title: 'Diagnostic Validation',
    } as any);
  }

  return blocks;
}

/**
 * Detect markdown tables with numeric time-series data and convert to chart + surrounding text.
 * Returns null if no suitable table is found.
 */
function extractMarkdownTableAsChart(text: string): UIBlock[] | null {
  // Match markdown table: header row, separator row, data rows
  const tableRegex = /\|(.+)\|\n\|[-| :]+\|\n((?:\|.+\|\n?)+)/g;
  const match = tableRegex.exec(text);
  if (!match) return null;

  const headerLine = match[1];
  const dataBlock = match[2];
  const columns = headerLine.split('|').map(c => c.trim()).filter(Boolean);

  if (columns.length < 2) return null;

  const rows = dataBlock.trim().split('\n').map(row =>
    row.split('|').map(c => c.trim()).filter(Boolean)
  ).filter(r => r.length === columns.length);

  if (rows.length < 3) return null;

  // Check if first column looks like timestamps (large numbers) and second is numeric
  const isTimeSeries = rows.every(r => {
    const first = parseFloat(r[0]);
    const second = parseFloat(r[1]);
    return !isNaN(first) && !isNaN(second) && first > 1000000000;
  });

  if (!isTimeSeries) return null;

  // Build chart data
  const xKey = columns[0];
  const yKeys = columns.slice(1);
  const chartData = rows.map(r => {
    const obj: Record<string, any> = {};
    // Convert timestamp to readable time
    const ts = parseFloat(r[0]);
    obj[xKey] = new Date(ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    yKeys.forEach((key, i) => { obj[key] = parseFloat(r[i + 1]); });
    return obj;
  });

  const blocks: UIBlock[] = [];

  // Text before the table
  const tableStart = text.indexOf(match[0]);
  const before = text.slice(0, tableStart).trim();
  if (before) blocks.push({ type: 'text', text: before, markdown: true });

  // Chart block
  blocks.push({
    type: 'chart',
    chartType: 'line',
    data: chartData,
    xKey,
    yKeys,
  } as any);

  // Text after the table
  const after = text.slice(tableStart + match[0].length).trim();
  if (after) blocks.push({ type: 'text', text: after, markdown: true });

  return blocks;
}

/**
 * Safe wrapper — never throws
 */
export function safeParseResponse(raw: any): UIBlock[] {
  try {
    return adaptAgentCoreResponse(raw);
  } catch {
    return [{ type: 'json', data: raw }];
  }
}
